import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sendApiRequest } from '../utils/api';
import { FaBook, FaClock, FaRupeeSign, FaCalendarAlt, FaStar, FaGraduationCap, FaSearch, FaFilter } from 'react-icons/fa';

const Exams = () => {
  const [exams, setExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredExams, setFilteredExams] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchExams = async () => {
      try {
        const response = await sendApiRequest('api/admin/exams', {}, 'GET')
        if (response.status) {
          setExams(response.data)
          setFilteredExams(response.data)
        }
      } catch (err) {
        console.log(err)
      }
    }

    fetchExams()
  }, [])

  useEffect(() => {
    const filtered = exams.filter(exam =>
      exam.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exam.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exam.category?.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredExams(filtered);
  }, [searchTerm, exams]);

  // Helper function to format date
  const formatLastDate = (createdAt, days) => {
    const createdDate = new Date(createdAt);
    const lastDate = new Date(createdDate);
    lastDate.setDate(createdDate.getDate() + days);
    return lastDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to get exam status
  const getExamStatus = (createdAt, days) => {
    const lastDate = new Date(createdAt);
    lastDate.setDate(lastDate.getDate() + days);
    const today = new Date();
    return today <= lastDate ? 'Open' : 'Closed';
  };

  // Get status color and icon
  const getStatusInfo = (status) => {
    if (status === 'Open') {
      return { color: 'from-green-500 to-emerald-600', icon: '🟢', text: 'Enrollment Open' };
    } else {
      return { color: 'from-red-500 to-pink-600', icon: '🔴', text: 'Enrollment Closed' };
    }
  };

  return (
    <div className="min-h-screen pt-4 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Available Courses</h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Discover comprehensive courses designed to help you excel in competitive exams.
            Start your preparation journey with expert-curated content and practice materials.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8 max-w-2xl mx-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search courses by name, description, or category..."
              className="block w-full pl-10 pr-4 py-4 border border-gray-300 rounded-2xl bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 placeholder-gray-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <FaFilter className="text-gray-400" />
            </div>
          </div>
        </div>

        {/* Exams Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredExams.map((exam) => {
            const lastDate = formatLastDate(exam.createdAt, exam.timingPeriod);
            const status = getExamStatus(exam.createdAt, exam.timingPeriod);
            const statusInfo = getStatusInfo(status);

            return (
              <div key={exam._id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100 overflow-hidden group hover:-translate-y-2">
                {/* Status Ribbon */}
                <div className={`absolute top-4 right-4 z-10 bg-gradient-to-r ${statusInfo.color} text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg`}>
                  {statusInfo.text}
                </div>

                {/* Image Section */}
                <div className="relative h-48 w-full overflow-hidden">
                  <img
                    src={exam.thumbnail}
                    alt={exam.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-80"></div>
                  <div className="absolute bottom-4 left-4">
                    <span className="px-3 py-1 bg-blue-500 text-white text-sm font-medium rounded-full shadow-lg">
                      {exam.category?.title || 'General Course'}
                    </span>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {exam.name}
                  </h2>
                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">
                    {exam.description}
                  </p>

                  {/* Course Details */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-gray-600">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <FaBook className="text-blue-600 text-sm" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">Batch Type</span>
                        <p className="text-xs text-gray-500">{exam.batchType}</p>
                      </div>
                    </div>

                    <div className="flex items-center text-gray-600">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <FaClock className="text-green-600 text-sm" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">Course Duration</span>
                        <p className="text-xs text-gray-500">{exam.timingPeriod} days</p>
                      </div>
                    </div>

                    <div className="flex items-center text-gray-600">
                      <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                        <FaRupeeSign className="text-yellow-600 text-sm" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">Course Fee</span>
                        <p className="text-xs text-gray-500">₹{exam.fee}</p>
                      </div>
                    </div>

                    <div className="flex items-center text-gray-600">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                        <FaCalendarAlt className="text-purple-600 text-sm" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">Last Date</span>
                        <p className="text-xs text-gray-500">{lastDate}</p>
                      </div>
                    </div>
                  </div>

                  {/* Category Features */}
                  {exam.category?.features && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <FaStar className="text-yellow-500" />
                        Key Features
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {exam.category.features.slice(0, 3).map((feature, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <Link
                      to={`/exams/${exam._id}`}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-center shadow-lg hover:shadow-xl"
                    >
                      View Details
                    </Link>
                    {status === 'Open' && (
                      <Link
                        to={`/exams/${exam._id}`}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 text-center shadow-lg hover:shadow-xl"
                      >
                        Enroll Now
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredExams.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaSearch className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-3">No courses found</h3>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              We couldn't find any courses matching your search. Try adjusting your search terms or browse all available courses.
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-xl font-medium transition-colors"
            >
              View All Courses
            </button>
          </div>
        )}

        {/* Study Guidance Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-blue-100">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center gap-3">
            <FaGraduationCap className="text-blue-600" />
            Study Success Guide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">📚 Preparation Tips</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-600">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-blue-600 text-sm font-bold">1</span>
                  </div>
                  <span>Create a consistent study schedule and stick to it</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-blue-600 text-sm font-bold">2</span>
                  </div>
                  <span>Practice with previous year question papers</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-blue-600 text-sm font-bold">3</span>
                  </div>
                  <span>Take regular mock tests to track your progress</span>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">🎯 Exam Strategy</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-600">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm font-bold">1</span>
                  </div>
                  <span>Focus on your weak areas and improve systematically</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm font-bold">2</span>
                  </div>
                  <span>Learn time management for better exam performance</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm font-bold">3</span>
                  </div>
                  <span>Stay updated with current affairs and general knowledge</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Exams;